if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          pagination: this.querySelector('.product-media-gallery__pagination'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        // Initialize pagination dots and arrows
        if (this.elements.pagination) {
          this.initPagination();
          // Set initial active pagination dot
          const activeSlide = this.elements.viewer.querySelector('.slider__slide.is-active');
          if (activeSlide && activeSlide.dataset.mediaId) {
            this.setActivePaginationDot(activeSlide.dataset.mediaId);
          }
        }
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        
        // Update active pagination dot
        if (this.elements.pagination) {
          this.setActivePaginationDot(event.detail.currentElement.dataset.mediaId);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Update active pagination dot
        if (this.elements.pagination) {
          this.setActivePaginationDot(mediaId);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initPagination() {
        const dots = this.elements.pagination.querySelectorAll('.product-media-gallery__pagination-dot');
        const prevArrow = this.elements.pagination.querySelector('.product-media-gallery__pagination-arrow--prev');
        const nextArrow = this.elements.pagination.querySelector('.product-media-gallery__pagination-arrow--next');

        // Add click handlers to pagination dots
        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const mediaId = dot.dataset.target;
            if (mediaId) {
              this.setActiveMedia(mediaId, false);
            }
          });
        });

        // Add click handlers to pagination arrows
        if (prevArrow) {
          prevArrow.addEventListener('click', () => {
            this.navigatePagination('prev');
          });
        }

        if (nextArrow) {
          nextArrow.addEventListener('click', () => {
            this.navigatePagination('next');
          });
        }
      }

      setActivePaginationDot(mediaId) {
        if (!this.elements.pagination) return;
        
        const dots = this.elements.pagination.querySelectorAll('.product-media-gallery__pagination-dot');
        dots.forEach((dot) => {
          dot.classList.remove('active');
          dot.removeAttribute('aria-current');
        });

        const activeDot = this.elements.pagination.querySelector(`[data-target="${mediaId}"]`);
        if (activeDot) {
          activeDot.classList.add('active');
          activeDot.setAttribute('aria-current', 'true');
        }
      }

      navigatePagination(direction) {
        const dots = Array.from(this.elements.pagination.querySelectorAll('.product-media-gallery__pagination-dot'));
        const activeDot = this.elements.pagination.querySelector('.product-media-gallery__pagination-dot.active');
        
        if (!activeDot || dots.length === 0) return;

        const currentIndex = dots.indexOf(activeDot);
        let targetIndex;

        if (direction === 'prev') {
          targetIndex = currentIndex > 0 ? currentIndex - 1 : dots.length - 1;
        } else {
          targetIndex = currentIndex < dots.length - 1 ? currentIndex + 1 : 0;
        }

        const targetDot = dots[targetIndex];
        if (targetDot && targetDot.dataset.target) {
          this.setActiveMedia(targetDot.dataset.target, false);
        }
      }
    }
  );
}
